import {
  createFileRoute,
  useNavigate,
  useLocation,
} from '@tanstack/react-router'
import { requirePermission } from '@/lib/permissions/permissions'
import { ConversationList } from '@/components/chat/conversation-list'
import { UserConversationsRow } from '@/lib/hooks/useConversations'
import { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils/ui'
import {
  Building2,
  ArrowLeft,
  PanelLeftClose,
  ChevronRight,
  EllipsisVertical,
  ExternalLink,
  Megaphone,
  MessageCircle,
  MessageCirclePlus,
  Loader2,
  PanelRightOpen,
  Pencil,
  Search,
  User,
  UserMinus,
  UserPlus,
  Users,
  X,
} from 'lucide-react'

import { useConversationListRealtime } from '@/lib/hooks/useConversationListRealtime'
import { useMarkConversationRead } from '@/lib/hooks/useMarkConversationRead'
import { RealtimeChat } from '@/components/chat/realtime-chat'
import { useIsTablet } from '@/hooks/use-mobile'
import { useMediaQuery } from '@/lib/hooks/useMediaQuery'
import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar'
import { useClientChatFeature } from '@/lib/hooks/useClientChatFeature'
import {
  filterActiveChatClients,
  mergeNewChatDirectTargets,
  upsertConversationInList,
  type NewChatDirectTarget,
} from '@/lib/utils/chatClients'
import {
  createConversation,
  createConversationWithClient,
  updateConversation,
} from '@/lib/utils/conversationUtils'
import { AnnouncementDialog } from '@/components/chat/AnnouncementDialog'
import { ChatFeatureBanner } from '@/components/chat/ChatFeatureBanner'
import { ClientQuickOverviewPanel } from '@/components/client/ClientQuickOverviewPanel'
import { useNavbar } from '@/components/navbar'
import { useOrg, useOrgs } from '@/lib/states/session/hooks'
import { trpc } from '@/lib/trpc/client'
import { trpcUtils } from '@/lib/trpc/vanilla'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { Checkbox } from '@/components/ui/checkbox'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select'

function deriveConversationName(
  conversation: UserConversationsRow | null,
  viewerUserId: string,
) {
  if (!conversation) return 'Chat'
  if (conversation.conversation_name?.trim()) {
    return conversation.conversation_name
  }
  const names = (conversation.members ?? [])
    .filter((member) => member.user_id !== viewerUserId)
    .map((member) =>
      `${member.first_name || ''} ${member.last_name || ''}`.trim(),
    )
    .filter(Boolean)
  return names.length ? names.join(', ') : 'Chat'
}

function deriveConversationAvatarUrl(
  conversation: UserConversationsRow | null,
  viewerUserId: string,
) {
  if (!conversation) return undefined
  const memberCount = Array.isArray(conversation.members)
    ? conversation.members.length
    : 0
  if (memberCount > 2) return undefined
  const others = (conversation.members ?? []).filter(
    (member) => member.user_id !== viewerUserId,
  )
  if (!others.length) return undefined
  return others[0]?.avatar?.url
}

function ConversationSearchFilters({
  search,
  onSearchChange,
  orgFilter,
  onOrgFilterChange,
  orgs,
  showOrgFilter,
}: {
  search: string
  onSearchChange: (value: string) => void
  orgFilter: string
  onOrgFilterChange: (value: string) => void
  orgs: { id: string; name: string; logoUrl?: string }[]
  showOrgFilter: boolean
}) {
  const selectedOrg =
    orgFilter === 'all' ? undefined : orgs.find((org) => org.id === orgFilter)

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search conversations"
          className="pl-9"
        />
      </div>
      {showOrgFilter ? (
        <Select value={orgFilter} onValueChange={onOrgFilterChange}>
          <SelectTrigger className="h-8 text-xs">
            <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
              {selectedOrg ? (
                <Avatar className="h-4 w-4 shrink-0 rounded-sm">
                  <AvatarImage
                    src={selectedOrg.logoUrl}
                    alt={selectedOrg.name}
                    className="object-cover"
                  />
                  <AvatarFallback className="rounded-sm text-[9px]">
                    {selectedOrg.name[0] ?? 'O'}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <span className="truncate">
                {selectedOrg?.name ?? 'All organisations'}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" indicatorPosition="right">
              <span className="flex min-w-0 items-center gap-2">
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">All organisations</span>
              </span>
            </SelectItem>
            {orgs.map((item) => (
              <SelectItem
                key={item.id}
                value={item.id}
                indicatorPosition="right"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Avatar className="h-4 w-4 rounded-sm">
                    <AvatarImage
                      src={item.logoUrl}
                      alt={item.name}
                      className="object-cover"
                    />
                    <AvatarFallback className="rounded-sm text-[9px]">
                      {item.name[0] ?? 'O'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate">{item.name}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
    </div>
  )
}

export const Route = createFileRoute('/_auth/_dashboard/chat')({
  beforeLoad: () => {
    requirePermission('messages.read')
  },
  component: RouteComponent,
  loader: async ({ context }) => {
    const id = context.session?.user.id
    if (!id) {
      throw new Error('User not found')
    }
    await trpcUtils.chat.listConversations.ensureData()
    return id
  },
})

function RouteComponent() {
  const { setHidden } = useNavbar()
  const userId = Route.useLoaderData()
  const isTablet = useIsTablet()
  const isBelowWideDesktop = useMediaQuery('(max-width: 1399px)')
  const canShowClientSidebar = useMediaQuery('(min-width: 900px)')
  const navigate = useNavigate()
  const org = useOrg()
  const orgs = useOrgs()
  const orgId = org?.id
  const showOrgFilter = orgs.length > 1
  const utils = trpc.useUtils()

  const { setOpen, isMobile } = useSidebar()

  useEffect(() => {
    if (isTablet) {
      setOpen(false)
    }
  }, [isTablet, setOpen])

  useEffect(() => {
    setHidden(isMobile)
    return () => setHidden(false)
  }, [isMobile, setHidden])

  useLayoutEffect(() => {
    const mainScrollableArea = document.getElementById('main-scrollable-area')
    if (!mainScrollableArea || !isMobile) {
      return
    }

    const previousOverflowY = mainScrollableArea.style.overflowY
    const previousOverscrollBehaviorY =
      mainScrollableArea.style.overscrollBehaviorY

    mainScrollableArea.style.overflowY = 'hidden'
    mainScrollableArea.style.overscrollBehaviorY = 'contain'

    return () => {
      mainScrollableArea.style.overflowY = previousOverflowY
      mainScrollableArea.style.overscrollBehaviorY = previousOverscrollBehaviorY
    }
  }, [isMobile])

  const [selectedConversation, setSelectedConversation] =
    useState<UserConversationsRow | null>(null)
  const [showConversationList, setShowConversationList] = useState(true)
  const [clientId, setClientId] = useState<string | null>(null)
  const [newConversationOpen, setNewConversationOpen] = useState(false)
  const [conversationSearch, setConversationSearch] = useState('')
  const [orgFilter, setOrgFilter] = useState('all')
  const [newChatStage, setNewChatStage] = useState<
    'select' | 'groupMembers' | 'groupDetails'
  >('select')
  const [newChatSearch, setNewChatSearch] = useState('')
  const [manageConversationOpen, setManageConversationOpen] = useState(false)
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<
    string[]
  >([])
  const [conversationNameInput, setConversationNameInput] = useState('')
  const [manageConversationNameInput, setManageConversationNameInput] =
    useState('')
  const [groupMembersOpen, setGroupMembersOpen] = useState(false)
  const [groupMembersSearch, setGroupMembersSearch] = useState('')
  const [addMembersOpen, setAddMembersOpen] = useState(false)
  const [addMembersSearch, setAddMembersSearch] = useState('')
  const [addMemberIds, setAddMemberIds] = useState<string[]>([])
  const [isCreatingConversation, setIsCreatingConversation] = useState(false)
  const [
    creatingDirectConversationUserId,
    setCreatingDirectConversationUserId,
  ] = useState<string | null>(null)
  const [isUpdatingConversation, setIsUpdatingConversation] = useState(false)
  const [isUpdatingMembers, setIsUpdatingMembers] = useState(false)
  const [removingMemberUserId, setRemovingMemberUserId] = useState<
    string | null
  >(null)
  const [pendingConversationIdSelection, setPendingConversationIdSelection] =
    useState<string | null>(null)
  const [announcementOpen, setAnnouncementOpen] = useState(false)
  const [clientProfilePanelOpen, setClientProfilePanelOpen] = useState(false)
  const [conversationListExpanded, setConversationListExpanded] =
    useState(false)

  const {
    data: conversations,
    isLoading,
    error,
  } = trpc.chat.listConversations.useQuery()

  const { data: clients } = trpc.chat.listClients.useQuery(
    { includeArchived: true },
    { enabled: !!orgId },
  )

  const { data: createOrgMembers } = trpc.chat.listOrgMembers.useQuery(
    { orgId: orgId! },
    { enabled: !!orgId },
  )

  const selectedConversationOrgId = selectedConversation?.org_id ?? null

  const { data: manageOrgMembers } = trpc.chat.listOrgMembers.useQuery(
    { orgId: selectedConversationOrgId! },
    { enabled: !!selectedConversationOrgId },
  )

  useConversationListRealtime({
    userId,
    currentViewingConversation: selectedConversation?.conversation_id,
  })

  useMarkConversationRead(selectedConversation?.conversation_id, userId)

  const safeConversations: UserConversationsRow[] = useMemo(() => {
    if (!conversations || error) return []
    return conversations
  }, [conversations, error])
  const selectedConversationIsGroup = useMemo(
    () =>
      selectedConversation?.is_group ??
      (selectedConversation?.members?.length ?? 0) > 2,
    [selectedConversation?.is_group, selectedConversation?.members?.length],
  )
  const selectedConversationName = useMemo(
    () => deriveConversationName(selectedConversation, userId),
    [selectedConversation, userId],
  )
  const selectedConversationAvatarUrl = useMemo(
    () => deriveConversationAvatarUrl(selectedConversation, userId),
    [selectedConversation, userId],
  )
  const selectedConversationInitials = useMemo(() => {
    const parts = selectedConversationName.split(' ').filter(Boolean)
    if (!parts.length) return '?'
    return parts
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('')
  }, [selectedConversationName])
  const selectedConversationParticipantNames = useMemo(() => {
    const entries = (selectedConversation?.members ?? []).map((member) => {
      const fullName =
        `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim()
      return [member.user_id, fullName || 'Unknown'] as const
    })
    return Object.fromEntries(entries)
  }, [selectedConversation?.members])

  const orgNameById = useMemo(
    () => new Map(orgs.map((item) => [item.id, item.name])),
    [orgs],
  )
  const selectedConversationOrgName = showOrgFilter
    ? orgNameById.get(selectedConversation?.org_id ?? '')
    : undefined

  const filteredConversations = useMemo(() => {
    const term = conversationSearch.trim().toLowerCase()

    return safeConversations.filter((conversation) => {
      if (orgFilter !== 'all' && conversation.org_id !== orgFilter) {
        return false
      }

      if (!term) {
        return true
      }

      const conversationName = (
        conversation.conversation_name ?? ''
      ).toLowerCase()
      const memberNames = (conversation.members ?? [])
        .map((member) =>
          `${member.first_name ?? ''} ${member.last_name ?? ''}`
            .trim()
            .toLowerCase(),
        )
        .join(' ')
      const lastMessage = (
        conversation.last_message?.body_text ?? ''
      ).toLowerCase()
      const conversationOrgName = (
        orgNameById.get(conversation.org_id) ?? ''
      ).toLowerCase()

      return (
        conversationName.includes(term) ||
        memberNames.includes(term) ||
        lastMessage.includes(term) ||
        conversationOrgName.includes(term)
      )
    })
  }, [conversationSearch, orgFilter, orgNameById, safeConversations])

  const safeClients = useMemo(() => clients ?? [], [clients])
  const selectableClients = useMemo(
    () => filterActiveChatClients(safeClients),
    [safeClients],
  )
  const safeAnnouncementClients = useMemo(
    () => selectableClients,
    [selectableClients],
  )
  const safeCreateOrgMembers = useMemo(
    () => createOrgMembers ?? [],
    [createOrgMembers],
  )
  const safeManageOrgMembers = useMemo(
    () => manageOrgMembers ?? [],
    [manageOrgMembers],
  )
  const selectedConversationMemberIds = useMemo(
    () =>
      new Set(
        (selectedConversation?.members ?? []).map((member) => member.user_id),
      ),
    [selectedConversation],
  )
  const selectedConversationMembersWithDetails = useMemo(() => {
    const detailsByUserId = new Map(
      safeManageOrgMembers.map((member) => [member.user_id, member]),
    )
    return (selectedConversation?.members ?? []).map((member) => {
      const details = detailsByUserId.get(member.user_id)
      const fullName =
        `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim() ||
        'Unknown'
      return {
        user_id: member.user_id,
        fullName,
        email: details?.user?.email ?? null,
        memberType: details?.member_type ?? null,
      }
    })
  }, [safeManageOrgMembers, selectedConversation])
  const filteredGroupMembers = useMemo(() => {
    const query = groupMembersSearch.trim().toLowerCase()
    if (!query) {
      return selectedConversationMembersWithDetails
    }
    return selectedConversationMembersWithDetails.filter((member) => {
      const name = member.fullName.toLowerCase()
      const email = (member.email ?? '').toLowerCase()
      return name.includes(query) || email.includes(query)
    })
  }, [groupMembersSearch, selectedConversationMembersWithDetails])
  const filteredAddMemberOptions = useMemo(() => {
    const query = addMembersSearch.trim().toLowerCase()
    if (!query) {
      return safeManageOrgMembers
    }
    return safeManageOrgMembers.filter((member) => {
      const fullName =
        `${member.user?.first_name ?? ''} ${member.user?.last_name ?? ''}`
          .trim()
          .toLowerCase()
      const email = (member.user?.email ?? '').toLowerCase()
      return fullName.includes(query) || email.includes(query)
    })
  }, [addMembersSearch, safeManageOrgMembers])
  const selectableMembers = useMemo(
    () => safeCreateOrgMembers.filter((member) => member.user_id !== userId),
    [safeCreateOrgMembers, userId],
  )

  const filteredSelectableMembers = useMemo(() => {
    const query = newChatSearch.trim().toLowerCase()
    if (!query) {
      return selectableMembers
    }

    return selectableMembers.filter((member) => {
      const fullName =
        `${member.user?.first_name ?? ''} ${member.user?.last_name ?? ''}`
          .trim()
          .toLowerCase()
      const email = (member.user?.email ?? '').toLowerCase()
      return fullName.includes(query) || email.includes(query)
    })
  }, [newChatSearch, selectableMembers])

  const selectableDirectTargets = useMemo(
    () =>
      mergeNewChatDirectTargets({
        clients: selectableClients,
        coaches: selectableMembers.filter(
          (member) => member.member_type === 'coach',
        ),
      }),
    [selectableClients, selectableMembers],
  )

  const filteredSelectableDirectTargets = useMemo(() => {
    const query = newChatSearch.trim().toLowerCase()
    if (!query) {
      return selectableDirectTargets
    }

    return selectableDirectTargets.filter((target) => {
      const fullName = `${target.firstName ?? ''} ${target.lastName ?? ''}`
        .trim()
        .toLowerCase()
      const email = (target.email ?? '').toLowerCase()
      return fullName.includes(query) || email.includes(query)
    })
  }, [newChatSearch, selectableDirectTargets])

  const hasNewChatOptions = selectableDirectTargets.length > 0

  // Check for client ID when 1:1 conversation is selected
  useEffect(() => {
    if (!selectedConversation || selectedConversationIsGroup) {
      setClientId(null)
      return
    }

    const members = Array.isArray(selectedConversation.members)
      ? selectedConversation.members
      : []

    if (members.length !== 2) {
      setClientId(null)
      return
    }

    const otherUserId = members.find(
      (member) => member.user_id !== userId,
    )?.user_id
    const matchingClient = safeClients.find(
      (client) => client.id === otherUserId,
    )
    setClientId(matchingClient?.id ?? null)
  }, [safeClients, selectedConversation, selectedConversationIsGroup, userId])

  const { data: isChatEnabledForClient, isLoading: isCheckingChatFeature } =
    useClientChatFeature(clientId)

  const handleSelectConversation = (conversation: UserConversationsRow) => {
    setSelectedConversation(conversation)
    setPendingConversationIdSelection(null)
    if (isMobile) {
      setShowConversationList(false)
    }
    const conversationId = conversation.conversation_id
    if (!conversationId) return
    void navigate({
      to: '/chat',
      search: { conversationId },
      replace: true,
    })
  }

  useEffect(() => {
    if (isMobile && selectedConversation) {
      setShowConversationList(false)
    }
  }, [isMobile, selectedConversation])

  const handleBackToConversations = () => {
    setShowConversationList(true)
  }

  const handleOpenClientProfile = () => {
    if (!clientId) return
    if (isMobile || !canShowClientSidebar) {
      navigate({ to: '/clients/$clientId', params: { clientId } })
      return
    }
    setClientProfilePanelOpen(true)
  }

  const handleOpenFullClientProfile = () => {
    if (!clientId) return
    navigate({ to: '/clients/$clientId', params: { clientId } })
  }

  const handleNewConversationOpenChange = (open: boolean) => {
    setNewConversationOpen(open)
    if (!open) {
      setNewChatStage('select')
      setNewChatSearch('')
      setConversationNameInput('')
      setSelectedParticipantIds([])
    }
  }

  const handleAnnouncementOpenChange = (open: boolean) => {
    setAnnouncementOpen(open)
  }

  if (error) {
    console.error('Error fetching conversations:', error)
  }

  const selectCreatedConversation = async (conversationId: string) => {
    const conversation = await utils.chat.getConversation.fetch({
      conversationId,
    })
    if (conversation) {
      utils.chat.listConversations.setData(undefined, (current) =>
        upsertConversationInList(current, conversation),
      )
      setSelectedConversation(conversation)
    }
    setPendingConversationIdSelection(conversationId)
    void utils.chat.listConversations.invalidate()
    return conversation
  }

  const handleCreateDirectConversation = async (
    target: NewChatDirectTarget,
  ) => {
    if (!userId || !orgId) {
      return
    }

    setCreatingDirectConversationUserId(target.userId)
    setIsCreatingConversation(true)
    try {
      const conversationId =
        target.kind === 'client'
          ? await createConversationWithClient({ clientId: target.userId })
          : await createConversation({
              participantUserIds: [userId, target.userId],
              isGroup: false,
            })

      const conversation = await selectCreatedConversation(conversationId)
      if (target.kind === 'client' && conversation) {
        utils.chat.getDirectConversation.setData(
          { clientUserId: target.userId },
          conversation,
        )
      }

      handleNewConversationOpenChange(false)
      navigate({
        to: '/chat',
        search: { conversationId },
      })
    } catch (err) {
      console.error('Failed to create direct conversation', err)
      toast.error('Failed to create conversation')
    } finally {
      setIsCreatingConversation(false)
      setCreatingDirectConversationUserId(null)
    }
  }

  const handleCreateGroupConversation = async () => {
    if (!userId || !orgId) {
      return
    }

    const participantUserIds = [...new Set([userId, ...selectedParticipantIds])]
    if (participantUserIds.length < 3) {
      toast.error('Select at least two members for a group')
      return
    }

    setIsCreatingConversation(true)
    try {
      const trimmedName = conversationNameInput.trim()
      const conversationId = await createConversation({
        orgId,
        participantUserIds,
        name: trimmedName.length > 0 ? trimmedName : undefined,
        isGroup: true,
      })

      await selectCreatedConversation(conversationId)

      handleNewConversationOpenChange(false)
      navigate({
        to: '/chat',
        search: { conversationId },
      })
    } catch (err) {
      console.error('Failed to create conversation', err)
      toast.error('Failed to create conversation')
    } finally {
      setIsCreatingConversation(false)
    }
  }

  const handleOpenNewGroupFlow = () => {
    setNewChatStage('groupMembers')
    setNewChatSearch('')
  }

  const handleGroupMembersNext = () => {
    if (selectedParticipantIds.length < 2) {
      toast.error('Select at least two members')
      return
    }
    setNewChatStage('groupDetails')
    setNewChatSearch('')
  }

  const toggleSelectedParticipant = (memberUserId: string) => {
    setSelectedParticipantIds((current) =>
      current.includes(memberUserId)
        ? current.filter((id) => id !== memberUserId)
        : [...current, memberUserId],
    )
  }

  const handleManageConversationOpenChange = (open: boolean) => {
    setManageConversationOpen(open)
    if (!open) {
      setManageConversationNameInput('')
      return
    }

    if (!selectedConversation) {
      return
    }

    setManageConversationNameInput(selectedConversation.conversation_name ?? '')
  }

  const handleUpdateConversation = async () => {
    if (!selectedConversation) {
      return
    }

    const nextName = manageConversationNameInput.trim()
    setIsUpdatingConversation(true)
    try {
      await updateConversation({
        conversationId: selectedConversation.conversation_id,
        name: nextName,
      })

      await utils.chat.listConversations.invalidate()
      setManageConversationOpen(false)
      toast.success('Conversation updated')
    } catch (err) {
      console.error('Failed to update conversation', err)
      toast.error('Failed to update conversation')
    } finally {
      setIsUpdatingConversation(false)
    }
  }

  const handleGroupMembersOpenChange = (open: boolean) => {
    setGroupMembersOpen(open)
    if (!open) {
      setGroupMembersSearch('')
    }
  }

  const handleAddMembersOpenChange = (open: boolean) => {
    setAddMembersOpen(open)
    if (!open) {
      setAddMembersSearch('')
      setAddMemberIds([])
    }
  }

  const toggleAddMemberSelection = (memberUserId: string) => {
    if (selectedConversationMemberIds.has(memberUserId)) {
      return
    }
    setAddMemberIds((current) =>
      current.includes(memberUserId)
        ? current.filter((id) => id !== memberUserId)
        : [...current, memberUserId],
    )
  }

  const handleAddMembersToGroup = async () => {
    if (!selectedConversation || addMemberIds.length === 0) {
      return
    }

    setIsUpdatingMembers(true)
    try {
      await updateConversation({
        conversationId: selectedConversation.conversation_id,
        addUserIds: addMemberIds,
      })
      await utils.chat.listConversations.invalidate()
      handleAddMembersOpenChange(false)
      toast.success('Members added')
    } catch (err) {
      console.error('Failed to add members', err)
      toast.error('Failed to add members')
    } finally {
      setIsUpdatingMembers(false)
    }
  }

  const handleRemoveMemberFromGroup = async (memberUserId: string) => {
    if (!selectedConversation) {
      return
    }

    setIsUpdatingMembers(true)
    setRemovingMemberUserId(memberUserId)
    try {
      await updateConversation({
        conversationId: selectedConversation.conversation_id,
        removeUserIds: [memberUserId],
      })
      await utils.chat.listConversations.invalidate()
      toast.success('Member removed')
    } catch (err) {
      console.error('Failed to remove member', err)
      toast.error('Failed to remove member')
    } finally {
      setIsUpdatingMembers(false)
      setRemovingMemberUserId(null)
    }
  }

  const location = useLocation()
  const lastUrlConversationId = useRef<string | null>(null)
  const selectedConversationRef = useRef(selectedConversation)
  selectedConversationRef.current = selectedConversation

  // Select a conversation from a pending id or a newly changed URL param.
  // Do not re-apply a stale conversationId after the user picks another chat.
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.searchStr || '')
      const urlConvId = params.get('conversationId')
      const convId = pendingConversationIdSelection ?? urlConvId
      if (!convId || !safeConversations.length) {
        return
      }

      const found = safeConversations.find((c) => c.conversation_id === convId)
      if (!found) {
        return
      }

      const urlChanged = urlConvId !== lastUrlConversationId.current
      lastUrlConversationId.current = urlConvId

      if (
        !pendingConversationIdSelection &&
        !urlChanged &&
        selectedConversationRef.current
      ) {
        return
      }

      if (
        found.conversation_id !==
        selectedConversationRef.current?.conversation_id
      ) {
        setSelectedConversation(found)
        setClientProfilePanelOpen(false)
      }
      if (pendingConversationIdSelection === convId) {
        setPendingConversationIdSelection(null)
      }
    } catch (err) {
      console.error('Failed to parse conversationId from URL', err)
    }
  }, [location.searchStr, pendingConversationIdSelection, safeConversations])

  // Keep selected conversation in sync with latest query data (e.g. renamed group)
  useEffect(() => {
    if (!selectedConversation?.conversation_id) {
      return
    }
    const refreshed = safeConversations.find(
      (conversation) =>
        conversation.conversation_id === selectedConversation.conversation_id,
    )
    if (!refreshed) {
      return
    }
    const identityUnchanged =
      refreshed.conversation_name === selectedConversation.conversation_name &&
      refreshed.is_group === selectedConversation.is_group &&
      refreshed.org_id === selectedConversation.org_id &&
      refreshed.members === selectedConversation.members
    if (identityUnchanged) {
      return
    }
    setSelectedConversation(refreshed)
  }, [safeConversations, selectedConversation])

  const newConversationDialog = (
    <Dialog
      open={newConversationOpen}
      onOpenChange={handleNewConversationOpenChange}
      analyticsName="chat_new_conversation_dialog"
    >
      <DialogContent className="ph-no-capture">
        <DialogHeader>
          <DialogTitle>
            {newChatStage === 'select'
              ? 'New chat'
              : newChatStage === 'groupMembers'
                ? 'Add group members'
                : 'New group'}
          </DialogTitle>
        </DialogHeader>
        {hasNewChatOptions ? (
          <div className="flex flex-col gap-3">
            {newChatStage === 'groupMembers' &&
            selectedParticipantIds.length > 0 ? (
              <div className="flex flex-wrap gap-2 rounded-md border p-2">
                {selectedParticipantIds.map((memberId) => {
                  const member = selectableMembers.find(
                    (item) => item.user_id === memberId,
                  )
                  const fullName =
                    `${member?.user?.first_name ?? ''} ${member?.user?.last_name ?? ''}`.trim() ||
                    'Unknown'
                  return (
                    <span
                      key={memberId}
                      className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs"
                    >
                      {fullName}
                      <button
                        type="button"
                        aria-label={`Remove ${fullName}`}
                        onClick={() => toggleSelectedParticipant(memberId)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )
                })}
              </div>
            ) : null}

            {newChatStage !== 'groupDetails' ? (
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={
                    newChatStage === 'select'
                      ? 'Search clients and coaches'
                      : 'Search members'
                  }
                  className="pl-9"
                  value={newChatSearch}
                  onChange={(event) => setNewChatSearch(event.target.value)}
                />
              </div>
            ) : null}

            {newChatStage === 'select' ? (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  className="flex items-center gap-3 rounded-md border p-3 text-left transition hover:bg-muted/50"
                  onClick={handleOpenNewGroupFlow}
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Users className="h-5 w-5" />
                  </span>
                  <span className="flex-1">
                    <span className="block text-sm font-medium">New group</span>
                    <span className="block text-xs text-muted-foreground">
                      Add multiple members
                    </span>
                  </span>
                </button>

                <div className="max-h-64 overflow-y-auto rounded-md border">
                  {filteredSelectableDirectTargets.map((target) => {
                    const firstName = target.firstName ?? ''
                    const lastName = target.lastName ?? ''
                    const fullName =
                      `${firstName} ${lastName}`.trim() || 'Unknown'
                    const email = target.email
                    const isCreatingThisConversation =
                      creatingDirectConversationUserId === target.userId
                    const initials = fullName
                      .split(' ')
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((part) => part[0]?.toUpperCase() ?? '')
                      .join('')

                    return (
                      <button
                        type="button"
                        key={target.userId}
                        className="flex w-full items-start gap-3 border-b p-3 text-left transition hover:bg-muted/50 last:border-b-0"
                        disabled={isCreatingConversation}
                        onClick={() =>
                          void handleCreateDirectConversation(target)
                        }
                      >
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                          {initials || '?'}
                        </span>
                        <span className="flex-1 text-sm">
                          <span className="font-medium">{fullName}</span>
                          {target.kind === 'coach' ? (
                            <span className="ml-2 text-xs text-muted-foreground">
                              Coach
                            </span>
                          ) : null}
                          {email ? (
                            <span className="block text-muted-foreground">
                              {email}
                            </span>
                          ) : null}
                        </span>
                        {isCreatingThisConversation ? (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Creating...
                          </span>
                        ) : null}
                      </button>
                    )
                  })}
                  {filteredSelectableDirectTargets.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground">
                      {newChatSearch.trim()
                        ? 'No people match that search'
                        : 'No clients or coaches in this organisation'}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}

            {newChatStage === 'groupMembers' ? (
              <div className="flex flex-col gap-2">
                <div className="max-h-64 overflow-y-auto rounded-md border">
                  {filteredSelectableMembers.map((member) => {
                    const firstName = member.user?.first_name ?? ''
                    const lastName = member.user?.last_name ?? ''
                    const fullName =
                      `${firstName} ${lastName}`.trim() || 'Unknown'
                    const email = member.user?.email
                    const isSelected = selectedParticipantIds.includes(
                      member.user_id,
                    )

                    return (
                      <label
                        key={member.user_id}
                        className="flex cursor-pointer items-center gap-3 border-b p-3 last:border-b-0"
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() =>
                            toggleSelectedParticipant(member.user_id)
                          }
                        />
                        <span className="flex-1 text-sm">
                          <span className="font-medium">{fullName}</span>
                          {email ? (
                            <span className="block text-muted-foreground">
                              {email}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    )
                  })}
                </div>
              </div>
            ) : null}

            {newChatStage === 'groupDetails' ? (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="conversation-name">Group Name</Label>
                  <Input
                    id="conversation-name"
                    placeholder="Group name"
                    value={conversationNameInput}
                    onChange={(event) =>
                      setConversationNameInput(event.target.value)
                    }
                  />
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No clients or coaches in this organisation to start a chat with.
          </p>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              if (newChatStage === 'select') {
                handleNewConversationOpenChange(false)
                return
              }
              if (newChatStage === 'groupMembers') {
                setNewChatStage('select')
                return
              }
              setNewChatStage('groupMembers')
            }}
          >
            {newChatStage === 'select' ? 'Cancel' : 'Back'}
          </Button>
          {newChatStage === 'groupMembers' ? (
            <Button
              onClick={handleGroupMembersNext}
              disabled={selectedParticipantIds.length < 2}
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : null}
          {newChatStage === 'groupDetails' ? (
            <Button
              onClick={handleCreateGroupConversation}
              disabled={
                isCreatingConversation || selectedParticipantIds.length < 2
              }
            >
              {isCreatingConversation ? 'Creating...' : 'Create group'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  const announcementDialog = (
    <AnnouncementDialog
      open={announcementOpen}
      onOpenChange={handleAnnouncementOpenChange}
      clients={safeAnnouncementClients}
    />
  )

  const manageConversationDialog = selectedConversation ? (
    <Dialog
      open={manageConversationOpen}
      onOpenChange={handleManageConversationOpenChange}
      analyticsName="chat_manage_conversation_dialog"
    >
      <DialogContent className="ph-no-capture">
        <DialogHeader>
          <DialogTitle>Edit group</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <Label htmlFor="manage-conversation-name">Conversation name</Label>
            <Input
              id="manage-conversation-name"
              placeholder="Optional"
              value={manageConversationNameInput}
              onChange={(event) =>
                setManageConversationNameInput(event.target.value)
              }
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleManageConversationOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateConversation}
            disabled={isUpdatingConversation}
          >
            {isUpdatingConversation ? 'Saving...' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ) : null

  const groupMembersDialog = selectedConversation ? (
    <Dialog
      open={groupMembersOpen}
      onOpenChange={handleGroupMembersOpenChange}
      analyticsName="chat_group_members_dialog"
    >
      <DialogContent className="ph-no-capture">
        <DialogHeader>
          <DialogTitle>Group members</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search members"
                className="pl-9"
                value={groupMembersSearch}
                onChange={(event) => setGroupMembersSearch(event.target.value)}
              />
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => handleAddMembersOpenChange(true)}
              disabled={isUpdatingMembers || safeManageOrgMembers.length === 0}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Add member
            </Button>
          </div>
          <div className="max-h-80 overflow-y-auto rounded-md border">
            {filteredGroupMembers.map((member) => {
              const isYou = member.user_id === userId
              const isRemoving = removingMemberUserId === member.user_id
              return (
                <div
                  key={member.user_id}
                  className={cn(
                    'group/member flex items-center justify-between border-b p-3 last:border-b-0',
                    isRemoving && 'opacity-70',
                  )}
                >
                  <span className="min-w-0 flex-1 text-sm">
                    <span className="block truncate font-medium">
                      {member.fullName}
                      {isYou ? ' (You)' : ''}
                    </span>
                    {member.email ? (
                      <span className="block truncate text-muted-foreground">
                        {member.email}
                      </span>
                    ) : null}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          'h-8 w-8 transition-opacity',
                          isMobile
                            ? 'opacity-100'
                            : 'opacity-0 group-hover/member:opacity-100 focus-visible:opacity-100',
                        )}
                        aria-label={`Actions for ${member.fullName}`}
                      >
                        <EllipsisVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {member.memberType === 'client' ? (
                        <>
                          <DropdownMenuItem
                            onClick={() =>
                              navigate({
                                to: '/clients/$clientId',
                                params: { clientId: member.user_id },
                              })
                            }
                          >
                            <User className="mr-2 h-4 w-4" />
                            Go to client profile
                            <ExternalLink className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      ) : null}
                      <DropdownMenuItem
                        variant="destructive"
                        disabled={isYou || isUpdatingMembers}
                        onClick={() =>
                          void handleRemoveMemberFromGroup(member.user_id)
                        }
                      >
                        <UserMinus className="mr-2 h-4 w-4" />
                        {isRemoving ? 'Removing...' : 'Remove from group'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  ) : null

  const addMembersDialog = selectedConversation ? (
    <Dialog
      open={addMembersOpen}
      onOpenChange={handleAddMembersOpenChange}
      analyticsName="chat_add_members_dialog"
    >
      <DialogContent className="ph-no-capture">
        <DialogHeader>
          <DialogTitle>Add group members</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search members"
              className="pl-9"
              value={addMembersSearch}
              onChange={(event) => setAddMembersSearch(event.target.value)}
            />
          </div>
          <div className="max-h-80 overflow-y-auto rounded-md border">
            {filteredAddMemberOptions.map((member) => {
              const firstName = member.user?.first_name ?? ''
              const lastName = member.user?.last_name ?? ''
              const fullName = `${firstName} ${lastName}`.trim() || 'Unknown'
              const email = member.user?.email
              const alreadyInGroup = selectedConversationMemberIds.has(
                member.user_id,
              )
              const isSelected =
                alreadyInGroup || addMemberIds.includes(member.user_id)

              return (
                <label
                  key={member.user_id}
                  className="flex cursor-pointer items-center gap-3 border-b p-3 last:border-b-0"
                >
                  <Checkbox
                    checked={isSelected}
                    disabled={alreadyInGroup || isUpdatingMembers}
                    onCheckedChange={() =>
                      toggleAddMemberSelection(member.user_id)
                    }
                  />
                  <span className="min-w-0 flex-1 text-sm">
                    <span className="block truncate font-medium">
                      {fullName}
                    </span>
                    {email ? (
                      <span className="block truncate text-muted-foreground">
                        {email}
                      </span>
                    ) : null}
                    {alreadyInGroup ? (
                      <span className="block text-xs text-muted-foreground">
                        Already in group
                      </span>
                    ) : null}
                  </span>
                </label>
              )
            })}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleAddMembersOpenChange(false)}
            disabled={isUpdatingMembers}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddMembersToGroup}
            disabled={isUpdatingMembers || addMemberIds.length === 0}
          >
            {isUpdatingMembers ? 'Adding...' : 'Add selected'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ) : null

  const conversationHeaderProfile = (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <Avatar className="h-9 w-9 rounded-full">
        <AvatarImage
          src={selectedConversationAvatarUrl}
          alt={selectedConversationName}
          className="object-cover"
        />
        <AvatarFallback>{selectedConversationInitials}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <h2 className="truncate text-sm font-semibold">
          {selectedConversationName}
        </h2>
        {selectedConversationOrgName ? (
          <p className="truncate text-xs text-muted-foreground">
            {selectedConversationOrgName}
          </p>
        ) : null}
      </div>
    </div>
  )

  const conversationHeader = selectedConversation ? (
    <div className="flex items-center gap-3 border-b bg-background px-3 py-2 sticky top-0 z-10">
      {isMobile ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBackToConversations}
          className="p-2"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
      ) : null}
      {conversationHeaderProfile}
      {selectedConversationIsGroup ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Conversation options"
            >
              <EllipsisVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => handleManageConversationOpenChange(true)}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit group
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setGroupMembersOpen(true)}>
                <Users className="mr-2 h-4 w-4" />
                Group members
              </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : clientId ? (
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {!isMobile ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleOpenClientProfile}
              aria-label="Open client quick view"
            >
              <PanelRightOpen className="h-4 w-4" />
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleOpenFullClientProfile}
            aria-label="Open client profile"
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>
      ) : null}
    </div>
  ) : null

  // Mobile layout - show either conversation list or chat view
  if (isMobile) {
    return (
      <>
        {newConversationDialog}
        {announcementDialog}
        {manageConversationDialog}
        {groupMembersDialog}
        {addMembersDialog}
        <div
          className="h-full min-h-0 flex flex-col bg-background overflow-hidden"
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
          }}
        >
          {showConversationList || !selectedConversation ? (
            // Conversation list view
            <div className="flex flex-col h-full min-h-0">
              <div className="border-b bg-background sticky top-0 z-10">
                <div className="flex items-center justify-between px-4 pt-3 pb-2">
                  <div className="flex items-center gap-3">
                    <SidebarTrigger />
                    <h1 className="font-semibold text-lg">Conversations</h1>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleAnnouncementOpenChange(true)}
                      disabled={!orgId || safeAnnouncementClients.length === 0}
                      aria-label="Send announcement"
                    >
                      <Megaphone className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleNewConversationOpenChange(true)}
                      disabled={!orgId || !hasNewChatOptions}
                      aria-label="Create conversation"
                    >
                      <MessageCirclePlus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="px-4 pb-3">
                  <ConversationSearchFilters
                    search={conversationSearch}
                    onSearchChange={setConversationSearch}
                    orgFilter={orgFilter}
                    onOrgFilterChange={setOrgFilter}
                    orgs={orgs}
                    showOrgFilter={showOrgFilter}
                  />
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                <ConversationList
                  conversations={filteredConversations}
                  selectedConversationId={
                    selectedConversation?.conversation_id || undefined
                  }
                  onSelectConversation={handleSelectConversation}
                  isLoading={isLoading}
                  emptyTitle={
                    orgFilter !== 'all'
                      ? 'No conversations in this organisation'
                      : 'No conversations yet'
                  }
                  emptyDescription={
                    orgFilter !== 'all'
                      ? 'Try another organisation or start a new chat'
                      : 'Start a conversation to see it here'
                  }
                />
              </div>
            </div>
          ) : selectedConversation ? (
            // Chat view
            <div className="flex flex-col h-full min-h-0">
              {conversationHeader}
              <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                {clientId &&
                  !isCheckingChatFeature &&
                  isChatEnabledForClient === false && (
                    <div className="p-4">
                      <ChatFeatureBanner
                        clientId={clientId}
                        clientName={
                          selectedConversation?.members &&
                          Array.isArray(selectedConversation.members)
                            ? selectedConversation.members
                                .filter(
                                  (member: any) => member.user_id !== userId,
                                )
                                .map((member: any) =>
                                  `${member.first_name || ''} ${member.last_name || ''}`.trim(),
                                )
                                .filter((name: string) => name)
                                .join(', ')
                            : undefined
                        }
                      />
                    </div>
                  )}
                <div className="flex-1 min-h-0">
                  <RealtimeChat
                    conversationId={selectedConversation!.conversation_id!}
                    userId={userId}
                    participantNamesByUserId={
                      selectedConversationParticipantNames
                    }
                  />
                </div>
              </div>
            </div>
          ) : (
            // No conversation selected - show empty state
            <div className="flex flex-col h-full min-h-0">
              <div className="flex items-center gap-3 p-4 border-b bg-background sticky top-0 z-10">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToConversations}
                  className="p-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-sm truncate">
                    Select a conversation
                  </h2>
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageCircle className="text-2xl" />
                  </div>
                  <h3 className="font-medium text-lg mb-2">
                    Select a conversation
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Choose a conversation from the list to start chatting
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    )
  }

  // Desktop layout - side-by-side
  const showClientProfilePanel =
    !isMobile &&
    canShowClientSidebar &&
    clientProfilePanelOpen &&
    Boolean(clientId)
  const compactConversationList =
    !isMobile &&
    showClientProfilePanel &&
    isBelowWideDesktop &&
    !conversationListExpanded

  return (
    <>
      {newConversationDialog}
      {announcementDialog}
      {manageConversationDialog}
      {groupMembersDialog}
      {addMembersDialog}
      <div className="ph-no-capture h-full min-h-0 flex flex-row overflow-hidden">
        <div
          className={cn(
            'h-full bg-background/75 border-r-secondary border-r flex flex-col min-h-0',
            compactConversationList
              ? 'w-16'
              : isTablet
                ? 'w-64'
                : 'w-80',
          )}
        >
          <div className="border-b bg-background/75 sticky top-0 z-10">
            <div
              className={cn(
                'flex items-center justify-between gap-2 px-4 pt-3 pb-2',
                compactConversationList && 'justify-center px-2',
              )}
            >
              {!compactConversationList ? (
                <h1 className="flex items-center gap-3 font-semibold text-lg">
                  Conversations
                </h1>
              ) : null}
              <div className="flex items-center gap-1">
                {compactConversationList ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Conversation actions"
                      >
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem
                        onClick={() => handleAnnouncementOpenChange(true)}
                        disabled={
                          !orgId || safeAnnouncementClients.length === 0
                        }
                      >
                        <Megaphone className="mr-2 h-4 w-4" />
                        Announcement
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleNewConversationOpenChange(true)}
                        disabled={!orgId || !hasNewChatOptions}
                      >
                        <MessageCirclePlus className="mr-2 h-4 w-4" />
                        New conversation
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : showClientProfilePanel && isBelowWideDesktop ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConversationListExpanded(false)}
                    aria-label="Collapse conversations"
                  >
                    <PanelLeftClose className="h-4 w-4" />
                  </Button>
                ) : null}
                {!compactConversationList ? (
                  <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleAnnouncementOpenChange(true)}
                  disabled={!orgId || safeAnnouncementClients.length === 0}
                  aria-label="Send announcement"
                >
                  <Megaphone className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleNewConversationOpenChange(true)}
                  disabled={!orgId || !hasNewChatOptions}
                  aria-label="Create conversation"
                >
                  <MessageCirclePlus className="h-4 w-4" />
                </Button>
                  </>
                ) : null}
              </div>
            </div>
            {!compactConversationList ? (
            <div className="px-4 pb-3">
              <ConversationSearchFilters
                search={conversationSearch}
                onSearchChange={setConversationSearch}
                orgFilter={orgFilter}
                onOrgFilterChange={setOrgFilter}
                orgs={orgs}
                showOrgFilter={showOrgFilter}
              />
            </div>
            ) : null}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <ConversationList
              conversations={filteredConversations}
              selectedConversationId={
                selectedConversation?.conversation_id || undefined
              }
              onSelectConversation={handleSelectConversation}
              isLoading={isLoading}
              compact={compactConversationList}
              emptyTitle={
                orgFilter !== 'all'
                  ? 'No conversations in this organisation'
                  : 'No conversations yet'
              }
              emptyDescription={
                orgFilter !== 'all'
                  ? 'Try another organisation or start a new chat'
                  : 'Start a conversation to see it here'
              }
            />
          </div>
        </div>
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col overflow-hidden',
            showClientProfilePanel && 'basis-0',
          )}
        >
          {selectedConversation ? (
            <>
              {conversationHeader}
              {clientId &&
                !isCheckingChatFeature &&
                isChatEnabledForClient === false && (
                  <div className="p-4">
                    <ChatFeatureBanner
                      clientId={clientId}
                      clientName={
                        selectedConversation?.members &&
                        Array.isArray(selectedConversation.members)
                          ? selectedConversation.members
                              .filter(
                                (member: any) => member.user_id !== userId,
                              )
                              .map((member: any) =>
                                `${member.first_name || ''} ${member.last_name || ''}`.trim(),
                              )
                              .filter((name: string) => name)
                              .join(', ')
                          : undefined
                      }
                    />
                  </div>
                )}
              <div className="flex-1 min-h-0">
                <RealtimeChat
                  conversationId={selectedConversation.conversation_id!}
                  userId={userId}
                  participantNamesByUserId={
                    selectedConversationParticipantNames
                  }
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="text-2xl" />
                </div>
                <h3 className="font-medium text-lg mb-2">
                  Select a conversation
                </h3>
                <p className="text-muted-foreground text-sm">
                  Choose a conversation from the list to start chatting
                </p>
              </div>
            </div>
          )}
        </div>
        {showClientProfilePanel ? (
          <aside className="h-full min-w-0 basis-0 flex-1 shrink-0 animate-in slide-in-from-right border-l bg-background">
            <div className="flex h-full min-h-0 flex-col">
              <div className="flex h-12 shrink-0 items-center justify-between border-b px-4">
                <h2 className="text-sm font-semibold">Client profile</h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setClientProfilePanelOpen(false)}
                  aria-label="Close client profile"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="min-h-0 flex-1">
                <ClientQuickOverviewPanel clientId={clientId!} />
              </div>
            </div>
          </aside>
        ) : null}
      </div>
    </>
  )
}
